import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const WebSocketContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const POLL_INTERVAL = 10000; // 10 seconds

export function WebSocketProvider({ children }) {
  const { user, token, isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3;
  const pollIntervalRef = useRef(null);
  const lastPollTimeRef = useRef(null);
  const usingPollingRef = useRef(false);
  const generationRef = useRef(0);

  const fetchStoredNotifications = useCallback(async () => {
    if (!isAuthenticated || !token) return;

    try {
      const response = await axios.get(`${API}/notifications?limit=20&unread_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const storedNotifications = response.data.notifications || [];
      setNotifications(prev => {
        const existingIds = new Set(prev.map(n => n.notification_id || n.id));
        const newNotifs = storedNotifications.filter(n => !existingIds.has(n.notification_id));
        return [...prev, ...newNotifs].slice(0, 50);
      });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [isAuthenticated, token]);

  const handleNotification = useCallback((data) => {
    const notif = data.notification || data;
    setNotifications(prev => {
      const existingIds = new Set(prev.map(n => n.notification_id || n.id));
      if (existingIds.has(notif.notification_id || notif.id)) return prev;
      return [notif, ...prev].slice(0, 50);
    });

    const { type, title, message } = notif;

    switch (type) {
      case 'shift_claimed':
        toast.success(title || 'Shift Claimed!', { description: message });
        break;
      case 'booking_confirmed':
        toast.success(title || 'Booking Confirmed!', { description: message });
        break;
      case 'new_lead':
        toast.info(title || 'New Lead!', { description: message });
        break;
      case 'payment_received':
        toast.success(title || 'Payment Received!', { description: message });
        break;
      case 'new_review':
        toast.info(title || 'New Review!', { description: message });
        break;
      default:
        if (title) toast(title, { description: message });
    }
  }, []);

  // Polling fallback — authoritative single loop: always clear any existing
  // interval before starting a new one so timers can never stack.
  const startPolling = useCallback(() => {
    if (!token) return;
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    usingPollingRef.current = true;
    lastPollTimeRef.current = new Date().toISOString();
    setIsConnected(true); // User is "online" via polling

    pollIntervalRef.current = setInterval(async () => {
      const tickStart = new Date().toISOString();
      try {
        const since = lastPollTimeRef.current;
        const response = await axios.get(`${API}/notifications/poll?since=${encodeURIComponent(since)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newNotifs = response.data.notifications || [];
        if (newNotifs.length > 0) {
          newNotifs.forEach(n => handleNotification(n));
        }
        // Advance the cursor every tick (not only when notifications arrive)
        // so the backend never re-scans an ever-growing window.
        lastPollTimeRef.current = tickStart;
      } catch (error) {
        // Silent fail for polling
      }
    }, POLL_INTERVAL);
  }, [token, handleNotification]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    usingPollingRef.current = false;
  }, []);

  const userId = user?.user_id;
  const userRole = user?.role;

  const connect = useCallback(() => {
    // Bail if a socket is already OPEN *or* still CONNECTING — otherwise an
    // unreachable WS endpoint orphans CONNECTING sockets whose async onclose
    // handlers each spawn their own reconnect/polling chain.
    if (!isAuthenticated || !userId ||
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const generation = generationRef.current;
    const isAdmin = userRole === 'admin';
    // WS lives under /api so it rides the same edge proxy as every API call.
    const wsUrl = `${WS_URL}/api/ws/${userId}?is_admin=${isAdmin}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (generation !== generationRef.current) { ws.close(); return; }
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
        stopPolling();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleNotification(data);
        } catch (e) {
          // Non-JSON (pong)
        }
      };

      ws.onclose = (event) => {
        // Stale socket from a previous session/generation — ignore entirely
        // so it can't restart reconnect or polling after disconnect().
        if (generation !== generationRef.current) return;
        console.log('WebSocket disconnected', event.code);
        if (wsRef.current === ws) wsRef.current = null;

        if (isAuthenticated && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else if (isAuthenticated) {
          // WebSocket failed after max attempts - switch to polling
          console.log('WebSocket unavailable, switching to polling fallback');
          startPolling();
        }
      };

      ws.onerror = () => {
        // Error handler - onclose will fire after this
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      startPolling();
    }
  }, [isAuthenticated, userId, userRole, handleNotification, startPolling, stopPolling]);

  const disconnect = useCallback(() => {
    // Invalidate every in-flight socket/timer from prior generations.
    generationRef.current += 1;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      try { wsRef.current.close(1000, 'User logged out'); } catch (e) { /* already closed */ }
      wsRef.current = null;
    }
    stopPolling();
    setIsConnected(false);
    reconnectAttempts.current = 0;
  }, [stopPolling]);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send('ping');
    }
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    setNotifications(prev =>
      prev.map(n => (n.id === notificationId || n.notification_id === notificationId) ? { ...n, read: true } : n)
    );

    if (token) {
      try {
        await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && userId) {
      connect();
      fetchStoredNotifications();
    } else {
      disconnect();
      setNotifications([]);
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, userId, connect, disconnect, fetchStoredNotifications]);

  // Heartbeat for WS
  useEffect(() => {
    if (!isConnected || usingPollingRef.current) return;

    const heartbeat = setInterval(() => {
      sendPing();
    }, 30000);

    return () => clearInterval(heartbeat);
  }, [isConnected, sendPing]);

  const value = {
    isConnected,
    notifications,
    unreadCount: notifications.filter(n => !n.read).length,
    clearNotifications,
    markAsRead,
    reconnect: connect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

export default WebSocketContext;
